'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Copy, Pencil } from 'lucide-react';
import { Button, ConfirmDialog, Spinner, LanguageSelect } from '@repo/components';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Code from '@mui/icons-material/Code';
import Typography from '@mui/material/Typography';
import FormComponent from '@nineeng/forms';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useRef, useState } from 'react';
import { TaskTemplateForm } from '@repo/forms';
import { TASK_SCOPES, TaskTemplate, taskTypes, taskTemplateSchema, LanguageCode, Settings } from '@repo/shared/domain';
import { EventEmitter, guardedClient as guardedRequest } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useConfig } from 'src/hooks/useConfig';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { getTaskTemplate } from 'src/api/admin/taskTemplates';
import { getForm, getFormBySlug } from 'src/api/admin/forms';
import { getSettings } from 'src/api/admin/settings';
import PlayitFormPanel from 'src/parts/task/PlayitFormPanel';
import SimpleFormPanel from 'src/parts/task/SimpleFormPanel';
import SimpleFormAgenticPanel from 'src/parts/task/SimpleFormAgenticPanel';
import { TranslationsDialog } from './TranslationsDialog';

// Helper to format duration
export const durationToString = (duration) => {
	const periods = {
		Y: 'year',
		M: 'month',
		D: 'day',
	};

	if (!duration) return '';
	const chars = duration.match(/[A-Z]/g);
	const number = duration.match(/[0-9]+/g);

	return `${number} ${periods[chars[1]]}${number > 1 ? 's' : ''}`;
};

// Helper to generate duplicate name
const getDuplicateName = (name: string): string => {
	const copyPattern = / \(Copy( \d+)?\)$/;
	const match = name.match(copyPattern);
	if (match) {
		const currentNumber = match[1] ? parseInt(match[1].trim()) : 1;
		return name.replace(copyPattern, ` (Copy ${currentNumber + 1})`);
	}
	return `${name} (Copy)`;
};

export const Route = createFileRoute('/_admin/config/tasks/$slug/')({
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { slug } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { config } = useConfig();
	const { user: authenticatedUser } = useAuthenticatedUser();
	const featureFlags = config?.featureFlags;

	const [language, setLanguage] = useState<LanguageCode | null>(null);
	const [form, setForm] = useState<any>(null);
	const [isSaved, setIsSaved] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [taskTemplateData, setTaskTemplateData] = useState<TaskTemplate | null>(null);
	const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [isDuplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
	const [isTranslationsDialogOpen, setTranslationsDialogOpen] = useState(false);

	const formContainerRef = useRef<HTMLDivElement>(null);
	useBreadcrumbLevel(0, 'Configuration', '/config/tasks');
	useBreadcrumbLevel(1, 'Tasks', '/config/tasks');
	useBreadcrumbLevel(2, taskTemplateData?.name || 'Task Detail');

	const isNineID =
		authenticatedUser?.email?.includes('@nineid.com') || authenticatedUser?.email?.includes('@getnineid.com');
	const usesPlayit = !!featureFlags?.USES_PLAYIT;

	const { data: taskTemplate, isLoading: isTaskLoading } = useQuery({
		queryKey: ['task-template', slug],
		queryFn: () => getTaskTemplate(slug),
	});

	const { data: roles = [] } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: getRolesForAutocomplete,
	});

	const { data: languagesSettings = {} } = useQuery({
		queryKey: ['settings', Settings.Languages],
		queryFn: () => getSettings(Settings.Languages),
	});

	const { data: liveForm } = useQuery({
		queryKey: ['form-translation-status', taskTemplate?.form],
		queryFn: () => getFormBySlug(taskTemplate?.form || ''),
		enabled: !!taskTemplate?.form,
		refetchInterval: (query) => {
			return query.state.data?.translationStatus === 'translating' ? 3000 : false;
		},
	});
	const isTranslating = liveForm?.translationStatus === 'translating';

	const formAvailableLanguages = useMemo(() => {
		return Array.from(
			new Set([
				form?.language,
				liveForm?.language,
				...(liveForm?.availableLanguages || []),
				...(form?.availableLanguages || []),
			]),
		).filter(Boolean) as LanguageCode[];
	}, [form, liveForm]);

	// Initialize task data and fetch form
	useMemo(() => {
		if (taskTemplate && !taskTemplateData) {
			setTaskTemplateData({ ...taskTemplate });
			if (taskTemplate.form) {
				getForm(taskTemplate.form).then((formData) => {
					setForm(formData);
					setLanguage(formData?.language || 'en');
				});
			}
		}
	}, [taskTemplate, taskTemplateData]);

	const isEditable = ['custom', 'group', 'event'].includes(taskTemplateData?.type || '');

	const fetchLatestFormData = useCallback(
		async (lng: LanguageCode) => {
			if (!taskTemplate?.form || taskTemplate.type === 'playit') return;
			try {
				const updatedForm = await getForm(taskTemplate.form, lng);
				setForm(updatedForm);
			} catch (_err) {
				console.error(_err);
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to fetch updated form data.' });
			}
		},
		[taskTemplate?.form, taskTemplate?.type],
	);

	const updateTaskTemplate = useCallback(
		async (body: any, noSuccessMessage?: boolean) => {
			if (!taskTemplateData) return;
			try {
				await guardedRequest.put(`/api/task-templates/${taskTemplateData.id}`, body, {
					schema: taskTemplateSchema,
				});
				setDialogOpen(false);
				if (!noSuccessMessage) {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Updated task',
					});
				}
				queryClient.invalidateQueries({ queryKey: ['task-template', slug] });
				await fetchLatestFormData(language as LanguageCode);
			} catch (_err) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to update task.',
				});
			} finally {
				setIsSaved(true);
			}
		},
		[taskTemplateData, queryClient, slug, fetchLatestFormData, language],
	);

	const changeLanguage = async (lng: LanguageCode) => {
		setLanguage(lng);
		await fetchLatestFormData(lng);
		setIsSaved(true);
	};

	const scrollContainerToTop = () => {
		if (formContainerRef.current) {
			formContainerRef.current.scrollTo(0, 0);
		}
	};

	const debouncedUpdate = useMemo(
		() =>
			debounce((data: any, noSuccessMessage?: boolean) => {
				updateTaskTemplate(data, noSuccessMessage);
			}, 350),
		[updateTaskTemplate],
	);

	const updateTaskTemplateDebounced = useCallback(
		(data: any, noSuccessMessage?: boolean) => debouncedUpdate(data, noSuccessMessage),
		[debouncedUpdate],
	);

	const handleFormChange = (formSpecs: any) => {
		if (!taskTemplateData) return;
		const data = { ...taskTemplateData, formSpecs };
		setTaskTemplateData(data);
		setIsSaved(false);
		updateTaskTemplateDebounced(data, true);
	};

	const handleDelete = async () => {
		if (!taskTemplate) return;
		await guardedRequest.del(`/api/task-templates/${taskTemplate.id}`);
		queryClient.invalidateQueries({ queryKey: ['task-templates'] });
		navigate({ to: '/config/tasks' });
	};

	const handleDuplicate = async () => {
		if (!taskTemplateData) return;
		let isError = false;
		const duplicateTaskTemplateData = { ...taskTemplateData };
		duplicateTaskTemplateData.name = getDuplicateName(taskTemplateData.name || '');

		try {
			const { body } = await guardedRequest.post('/api/task-templates/duplicate', {
				duplicateTaskTemplateData,
				originalTaskTemplateSlug: taskTemplateData.slug,
			});
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Task duplicated',
			});
			window.open(`/config/tasks/${(body as any).slug}`, '_blank');
		} catch {
			isError = true;
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to duplicate task. Did you enter all data correctly?',
			});
		}

		queryClient.invalidateQueries({ queryKey: ['task-templates'] });
		if (!isError) {
			setDuplicateDialogOpen(false);
		}
	};

	const openFormBuilder = () => {
		if (form?.slug) {
			navigate({ to: '/config/forms/$slugOrId/build', params: { slugOrId: form.slug }, search: { fromTask: 'true' } });
		}
	};

	const openTranslateTask = () => {
		if (form?.slug) {
			navigate({ to: '/config/tasks/$slug/translate', params: { slug: form.slug }, search: { fromTask: 'true' } });
		}
	};

	if (isTaskLoading || !taskTemplateData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<>
			<div style={{ width: '100%', padding: 32 }} className="nine-task-overrides">
				<TaskTemplateForm
					onSubmit={updateTaskTemplate}
					data={{ ...taskTemplateData }}
					isOpen={dialogOpen}
					onClose={() => setDialogOpen(false)}
					roles={roles}
					usesPlayit={usesPlayit}
				/>
				<Paper sx={{ marginBottom: 2, padding: 2 }} variant="outlined">
					<Grid container spacing={2}>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Name
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTemplateData?.name || '-'}
							</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Description
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTemplateData?.description || '-'}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<div className="flex w-full justify-end gap-2">
								{isNineID && (
									<Tooltip title={'Open form settings'} placement="left">
										<Button size="icon" variant="ghost" onClick={() => navigate({ to: `/system/forms/${form.slug}` })}>
											<Code />
										</Button>
									</Tooltip>
								)}
								<Button variant="outline-bg-white" onClick={() => setDuplicateDialogOpen(true)} size="icon">
									<Copy />
								</Button>
								{taskTemplateData?.rolesCount || taskTemplateData?.typesCount ? (
									<Tooltip
										title={'Task cannot be deleted because it is assigned to one or more roles or types.'}
										placement="left"
									>
										<span>
											<Button
												variant="outline-bg-white"
												size="icon"
												disabled={true}
												onClick={() => setDeleteDialogOpen(true)}
											>
												<Trash2 />
											</Button>
										</span>
									</Tooltip>
								) : (
									<Button variant="outline-bg-white" onClick={() => setDeleteDialogOpen(true)}>
										<Trash2 />
									</Button>
								)}
								<Button onClick={() => setDialogOpen(true)} variant="outline-bg-white">
									<Pencil />
									Edit task
								</Button>
							</div>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Type
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTypes.find((t) => t.id === taskTemplateData?.type)?.label || '-'}
							</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Valid For
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTemplateData?.validDuration ? durationToString(taskTemplateData?.validDuration) : '-'}
							</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Estimated Duration
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTemplateData?.estMinutes ? `${taskTemplateData?.estMinutes} minutes` : '-'}
							</Typography>
						</Grid>
						<Grid item xs={3}>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Access Scope
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{taskTemplateData.scope ? TASK_SCOPES[taskTemplateData.scope] || 'Everyone' : 'Everyone'}
							</Typography>
						</Grid>
					</Grid>
				</Paper>

				<div className="flex flex-row gap-2">
					{taskTemplateData.type === 'simple' && (
						<Paper className="mb-2 w-full p-4" variant="outlined">
							<SimpleFormPanel
								taskData={taskTemplateData}
								formSchema={form?.formSchema}
								onChange={handleFormChange}
								isSaved={isSaved}
								languages={[...new Set([form?.language || 'en', ...(form?.availableLanguages ?? [])])]}
							/>
						</Paper>
					)}

					{taskTemplateData.type === 'simple-agentic' && (
						<Paper className="mb-2 w-full p-4" variant="outlined">
							<SimpleFormAgenticPanel
								taskData={taskTemplateData}
								formSchema={form?.formSchema}
								onChange={handleFormChange}
								isSaved={isSaved}
								languages={[...new Set([form?.language || 'en', ...(form?.availableLanguages ?? [])])]}
							/>
						</Paper>
					)}

					{taskTemplateData.type === 'playit' && (
						<Paper className="mb-2 w-full p-4" variant="outlined">
							<PlayitFormPanel taskData={taskTemplateData} onChange={handleFormChange} isSaved={isSaved} />
						</Paper>
					)}

					<Paper className="mb-2 w-full p-4" variant="outlined">
						<div
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'space-between',
							}}
						>
							<Typography variant="h6" sx={{ mb: 2 }}>
								Preview
							</Typography>
							<div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
								{isTranslating && (
									<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
										<CircularProgress size={16} />
										<Typography variant="caption" color="text.secondary">
											Translating...
										</Typography>
									</div>
								)}
								{isEditable && (
									<Button variant="outline-bg-white" onClick={openTranslateTask} disabled={isTranslating}>
										manage translations
									</Button>
								)}
								{isSaved && isEditable && formAvailableLanguages.length >= 1 && (
									<LanguageSelect
										availableLanguages={formAvailableLanguages}
										language={language as LanguageCode}
										onLanguageChange={(lng) => changeLanguage(lng)}
										onTranslationsClick={isTranslating ? undefined : () => setTranslationsDialogOpen(true)}
										customTailwindClasses={{
											selectTrigger: {
												border: 'border-border',
												width: 'w-[180px]',
											},
											selectContent: {
												width: 'w-[180px]',
											},
										}}
									/>
								)}
								{isEditable && (
									<Button variant="outline-bg-white" onClick={openFormBuilder} disabled={isTranslating}>
										<Pencil />
										Edit Task Form
									</Button>
								)}
							</div>
						</div>
						<div
							style={{
								position: 'relative',
								minHeight: 610,
								overflowY: 'auto',
							}}
							ref={formContainerRef}
						>
							{form?.formSchema && isSaved && taskTemplateData.type !== 'playit' && (
								<FormComponent
									schema={form.formSchema}
									style={{ flexGrow: 1 }}
									selectedLanguage={language}
									onChange={console.log}
									onNext={scrollContainerToTop}
									onPrevious={scrollContainerToTop}
								/>
							)}

							{form?.formSchema && !isSaved && (
								<div className="min-h-[610px] flex justify-center items-center">
									<CircularProgress />
								</div>
							)}

							{(!form ||
								!form.formSchema ||
								form.formSchema?.sections?.length === 0 ||
								taskTemplateData.type === 'playit') && (
								<div
									style={{
										textAlign: 'center',
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										justifyContent: 'center',
										margin: '2rem',
										marginTop: 100,
										color: '#AAA',
									}}
								>
									<img
										style={{ filter: 'saturate(0)', marginBottom: '1rem' }}
										width={200}
										src="/public/empty-form.svg"
									/>
									{taskTemplateData.type !== 'playit' && (
										<p>
											Form not yet configured. <br /> Start by clicking &quot;Edit&quot; Task Form
										</p>
									)}
									{taskTemplateData.type === 'playit' && <p>This task type does not have a preview.</p>}
								</div>
							)}
						</div>
					</Paper>
				</div>
			</div>

			<ConfirmDialog
				isOpen={isDeleteDialogOpen}
				cancelAction={() => setDeleteDialogOpen(false)}
				confirmAction={handleDelete}
				title="Warning"
			>
				Are you sure you want to continue and delete this task?
			</ConfirmDialog>
			<ConfirmDialog
				isOpen={isDuplicateDialogOpen}
				cancelAction={() => setDuplicateDialogOpen(false)}
				confirmAction={handleDuplicate}
				title="Duplicate Task"
			>
				Are you sure you want to continue and duplicate this task?
			</ConfirmDialog>

			{isTranslationsDialogOpen && (
				<TranslationsDialog
					isOpen={isTranslationsDialogOpen}
					onClose={() => setTranslationsDialogOpen(false)}
					formId={taskTemplateData?.form}
					formAvailableLanguages={formAvailableLanguages}
					languagesSettings={languagesSettings}
				/>
			)}
		</>
	);
}
